import React, { useRef, useState } from "react";
import styled from "styled-components";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";

const STAGES = [
  {
    tag: "Kai · 改",
    kanji: "改",
    title: "Promena",
    text: "Sve počinje odlukom da nešto menjaš. Svaki trening, svako ponavljanje, svaka kap znoja.",
  },
  {
    tag: "Zen · 善",
    kanji: "善",
    title: "Na bolje",
    text: "Nije bitno koliko brzo — bitno je da je danas bolje nego juče. Disciplina pobeđuje motivaciju.",
  },
  {
    tag: "Kaizen · 改善",
    kanji: "改善",
    title: "1% bolje. Svaki dan.",
    text: "Filozofija utkana u svaki šav. Nosi se s ponosom — jer naporan rad zaslužuje da se vidi.",
  },
];

const KaizenSection = () => {
  const ref = useRef(null);
  const [stage, setStage] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setStage(v < 1 / 3 ? 0 : v < 2 / 3 ? 1 : 2);
  });

  return (
    <Section ref={ref} id="kaizen">
      <Sticky>
        <KanjiLayer>
          {STAGES.map((s, i) => (
            <Kanji key={i} $on={stage === i} $full={i === 2}>
              {s.kanji}
            </Kanji>
          ))}
        </KanjiLayer>

        {STAGES.map((s, i) => (
          <Stage key={i} $on={stage === i}>
            <Tag>{s.tag}</Tag>
            <Title>{s.title}</Title>
            <Text>{s.text}</Text>
          </Stage>
        ))}

        <BarTrack>
          <Bar style={{ scaleX: scrollYProgress }} />
        </BarTrack>
      </Sticky>
    </Section>
  );
};

export default KaizenSection;

const Section = styled.section`
  width: 100%;
  height: 180vh;
  position: relative;
  background: var(--bg1);
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KanjiLayer = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const Kanji = styled.span`
  grid-area: 1 / 1;
  font-family: var(--font-kanji);
  font-size: ${(props) =>
    props.$full ? "clamp(16rem, 30vh, 36rem)" : "clamp(20rem, 38vh, 43rem)"};
  line-height: 1;
  letter-spacing: ${(props) => (props.$full ? "0.08em" : "0")};
  color: ${(props) =>
    props.$full ? "rgba(254, 95, 85, 0.18)" : "rgba(254, 95, 85, 0.1)"};
  -webkit-text-stroke: ${(props) =>
    props.$full ? "0" : "2.5px rgba(254, 95, 85, 0.5)"};
  opacity: ${(props) => (props.$on ? 1 : 0)};
  transform: scale(${(props) => (props.$on ? 1 : 1.18)});
  transition: opacity 0.7s var(--ease), transform 0.9s var(--ease);
`;

const Stage = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(68rem, 92vw);
  padding: 0 2.4rem;
  text-align: center;
  transform: translate(-50%, ${(props) => (props.$on ? "-50%" : "-42%")});
  opacity: ${(props) => (props.$on ? 1 : 0)};
  transition: opacity 0.6s var(--ease), transform 0.8s var(--ease);
  pointer-events: none;
  z-index: 2;
`;

const Tag = styled.div`
  font-size: 1.2rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--red);
  font-family: "Poppins-Medium";
  margin-bottom: 1.4rem;
`;

const Title = styled.h3`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(3.4rem, 5.2vw, 5.6rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text);
  margin-bottom: 1.6rem;
`;

const Text = styled.p`
  color: var(--muted);
  font-size: 1.8rem;
  font-family: "Poppins-Regular";
  line-height: 1.6;
  max-width: 44rem;
  margin: 0 auto;
`;

const BarTrack = styled.div`
  position: absolute;
  bottom: 4.4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 18rem;
  height: 2px;
  background: var(--line2);
  z-index: 3;
`;

const Bar = styled(motion.div)`
  height: 100%;
  width: 100%;
  background: var(--red);
  transform-origin: left;
`;
